"use client";

import { forwardRef, useState } from "react";
import { cn } from "~/lib/utils";
import {
  Share04Icon,
  FileIcon,
  TableIcon,
  FileCodeIcon,
  CopyIcon,
  PrinterIcon,
  ChevronDownIcon
} from "~/components/icons";

export type ExportFormat = 'pdf' | 'excel' | 'csv' | 'copy' | 'print';

export interface ExportOption {
  value: ExportFormat;
  label: string;
  icon?: React.ReactNode;
}

export interface ExportButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onExport?: (format: ExportFormat) => void;
  options?: ExportOption[];
}

const defaultOptions: ExportOption[] = [
  {
    value: 'pdf',
    label: 'PDF',
    icon: <FileIcon className="h-4 w-4 text-[#60646c]" />
  },
  {
    value: 'excel',
    label: 'Excel',
    icon: <TableIcon className="h-4 w-4 text-[#60646c]" />
  },
  {
    value: 'csv',
    label: 'CSV',
    icon: <FileCodeIcon className="h-4 w-4 text-[#60646c]" />
  },
  {
    value: 'copy',
    label: 'Copy',
    icon: <CopyIcon className="h-4 w-4 text-[#60646c]" />
  },
  {
    value: 'print',
    label: 'Print',
    icon: <PrinterIcon className="h-4 w-4 text-[#60646c]" />
  },
];

const ExportButton = forwardRef<HTMLButtonElement, ExportButtonProps>(
  ({ className, onExport, options = defaultOptions, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleExport = (format: ExportFormat) => {
      setIsOpen(false);
      onExport?.(format);
    };

    return (
      <div className="relative">
        <button
          ref={ref}
          type="button"
          className={cn(
            "flex h-8 items-center justify-center gap-2 rounded border border-[rgba(0,78,154,0.47)] bg-white px-3 text-sm font-semibold leading-5 text-[rgba(0,50,97,0.7)] transition-colors hover:bg-[rgba(0,85,199,0.04)] cursor-pointer",
            className
          )}
          onClick={() => setIsOpen(!isOpen)}
          {...props}
        >
          <Share04Icon className="h-4 w-4" />
          Export as
          <ChevronDownIcon className="h-4 w-4" />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 top-full z-20 mt-1 w-[151px] rounded-lg border border-[rgba(0,0,51,0.06)] bg-white p-2 shadow-lg">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="flex h-8 w-full items-center gap-2 rounded px-3 text-left text-sm text-[#1c2024] transition-colors hover:bg-[#f9f9fb] cursor-pointer"
                  onClick={() => handleExport(option.value)}
                >
                  {option.icon}
                  <span className="flex-1">{option.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
);

ExportButton.displayName = "ExportButton";

export { ExportButton };

